import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { PLAY_LOCALES } from '@/lib/playLocales';

const LANG_RE = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;

interface ManageLocalesModalProps {
  isOpen: boolean;
  selected: string[];
  onSave: (locales: string[]) => void;
  onClose: () => void;
  saving?: boolean;
}

// Multi-select board of all Play locales. Checked = on the editor board.
// Order of `chosen` is preserved and becomes the board order.
export function ManageLocalesModal({ isOpen, selected, onSave, onClose, saving }: ManageLocalesModalProps) {
  const [chosen, setChosen] = useState<string[]>(selected);
  const [search, setSearch] = useState('');

  useEffect(() => { if (isOpen) setChosen(selected); }, [isOpen, selected]);

  const chosenSet = useMemo(() => new Set(chosen.map((l) => l.toLowerCase())), [chosen]);
  const q = search.trim().toLowerCase();

  const options = useMemo(
    () => PLAY_LOCALES.filter((l) => !q || l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q)),
    [q],
  );
  const customCode = search.trim();
  const showCustom = !!customCode
    && LANG_RE.test(customCode)
    && !PLAY_LOCALES.some((l) => l.code.toLowerCase() === customCode.toLowerCase());

  const toggle = (code: string) =>
    setChosen((prev) => prev.some((l) => l.toLowerCase() === code.toLowerCase())
      ? prev.filter((l) => l.toLowerCase() !== code.toLowerCase())
      : [...prev, code]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage locales" size="md">
      <p className="text-xs text-content-muted mb-2">
        Checked locales appear on the board. Unchecking a locale only removes it from the board —
        its drafts and the live Google Play listing are not deleted.
      </p>
      <input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search language or code…"
        className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm mb-2"
      />
      <div className="border border-border rounded-md max-h-72 overflow-y-auto mb-4">
        {options.map((l) => {
          const on = chosenSet.has(l.code.toLowerCase());
          return (
            <button
              key={l.code}
              onClick={() => toggle(l.code)}
              className={`w-full text-left px-3 py-1.5 text-sm flex justify-between items-center ${
                on ? 'bg-primary/10 text-content' : 'text-content hover:bg-surface-muted'
              }`}
            >
              <span><span className="mr-2">{on ? '☑' : '☐'}</span>{l.name}</span>
              <span className="text-content-muted">{l.code}</span>
            </button>
          );
        })}
        {showCustom && (
          <button
            onClick={() => toggle(customCode)}
            className="w-full text-left px-3 py-1.5 text-sm flex justify-between items-center text-content hover:bg-surface-muted"
          >
            <span><span className="mr-2">{chosenSet.has(customCode.toLowerCase()) ? '☑' : '☐'}</span>Use custom code</span>
            <span className="text-content-muted">{customCode}</span>
          </button>
        )}
        {options.length === 0 && !showCustom && (
          <p className="px-3 py-3 text-sm text-content-muted">No matching locales.</p>
        )}
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs text-content-muted">{chosen.length} on board</span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={() => onSave(chosen)}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}
